import { storage, STORAGE_KEYS } from '@/shared/lib';

const LATEST_APP_VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000;
const LATEST_APP_VERSION_CHECK_KEY = STORAGE_KEYS.lastVersionCheckAt;

/**
 * Reads the timestamp of the last app-version check kept in localStorage. Returns 0 outside the browser, when the stored value is not a number, or when it is older than the check interval, in which case the entry is also removed.
 *
 * @returns The stored check timestamp, or 0 when it is absent, invalid or expired.
 */
const getLastAppVersionCheckAt = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const storedValue = storage.get(LATEST_APP_VERSION_CHECK_KEY);
  const parsedValue = Number(storedValue);

  if (Number.isNaN(parsedValue)) {
    return 0;
  }

  const hasExpired = Date.now() - parsedValue >= LATEST_APP_VERSION_CHECK_INTERVAL_MS;

  if (hasExpired) {
    storage.remove(LATEST_APP_VERSION_CHECK_KEY);
    return 0;
  }

  return parsedValue;
};

/**
 * Records when the app-version check last ran. Writes the timestamp, defaulting to the current time, into localStorage as a string and does nothing outside the browser.
 *
 * @param timestamp - Epoch milliseconds to record; defaults to the current time.
 * @returns Nothing.
 */
const setLastAppVersionCheckAt = (timestamp = Date.now()) => {
  if (typeof window === 'undefined') {
    return;
  }

  storage.set(LATEST_APP_VERSION_CHECK_KEY, timestamp);
};

/**
 * Decides whether a socket-triggered app-version check is due. Compares now against the stored last-check timestamp and allows the check once the configured interval has elapsed.
 *
 * @returns True when the version-check interval has elapsed.
 */
const canRunSocketVersionCheck = () =>
  Date.now() - getLastAppVersionCheckAt() >= LATEST_APP_VERSION_CHECK_INTERVAL_MS;

export { getLastAppVersionCheckAt, setLastAppVersionCheckAt, canRunSocketVersionCheck };
